import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import { SharePayload } from "@shared/type/ShareType";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import { apiFetch } from "@shared/api/api";
import { global, onSearchStore } from "@shared/libs/Global";
import { copyText, selectElementText } from "@shared/utils/ClipboardUtils";
import SnButton from "@shared/components/SnButton";

type ShareButtonProps = {
  menu: any;
  componentRef?: any;
};

// 공유 링크 유효기간(일). 생성 요청과 사용자 안내 문구가 어긋나지 않도록 한 곳에서 관리한다.
const SHARE_TTL_DAYS = 7;

const EXPIRE_NOTICE = `이 링크는 ${SHARE_TTL_DAYS}일 후 만료됩니다.`;

// 클립보드 복사가 막힌 환경(http 등)에서 링크를 직접 복사/열 수 있게 안내한다.
const ShareLinkFallback: React.FC<{ url: string }> = ({ url }) => {
  const linkRef = React.useRef<HTMLDivElement>(null);

  // 열릴 때 링크를 미리 선택해 두면 Ctrl+C로 바로 복사할 수 있다
  React.useEffect(() => {
    selectElementText(linkRef.current);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        fontSize: 13,
        padding: "4px 2px",
      }}
    >
      <div style={{ color: "#333", lineHeight: 1.6 }}>
        클립보드 복사에 실패했습니다. 아래 링크를 Ctrl+C로 복사하거나 새 창으로
        열어 주세요.
      </div>
      <div
        ref={linkRef}
        onClick={() => selectElementText(linkRef.current)}
        style={{
          background: "#f6f8fa",
          border: "1px solid #e1e4e8",
          borderRadius: 6,
          padding: "10px 12px",
          wordBreak: "break-all",
          fontFamily:
            "ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace",
          fontSize: 13,
          color: "#111",
          cursor: "text",
        }}
      >
        {url}
      </div>
      <div style={{ color: "#888", fontSize: 12 }}>· {EXPIRE_NOTICE}</div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 2,
        }}
      >
        <SnButton
          text="복사하기"
          onClick={async () => {
            // 버튼 클릭은 사용자 제스처라 execCommand 폴백까지 동작한다
            if (await copyText(url)) {
              await showDialog("success", "링크가 복사되었습니다.");
              return;
            }
            selectElementText(linkRef.current);
            await showDialog("alter", "Ctrl+C로 복사해 주세요.");
          }}
        />
        <SnButton
          text="새 창으로 열기"
          onClick={() => window.open(url, "_blank")}
        />
      </div>
    </div>
  );
};

// 탭 툴바에서 사용하는 공유 버튼.
// 페이지 컴포넌트가 getShareState()를 제공하면 조회/그리드 상태까지 포함해 링크를 만든다.
export const ShareButton: React.FC<ShareButtonProps> = ({
  menu,
  componentRef,
}) => {
  const handleClick = async () => {
    if (!menu) {
      await showDialog("alter", "공유할 메뉴가 없습니다.");
      return;
    }

    let shareState: any;
    try {
      const refInstance = componentRef?.current ?? componentRef;
      if (refInstance && typeof refInstance.getShareState === "function") {
        shareState = await refInstance.getShareState();
      } else {
        // getShareState를 제공하지 않는 화면은 공통 조회패널 값으로 대체한다.
        // 공유 버튼을 누른 탭이 활성 탭이 아닐 수 있으므로 활성 메뉴 기준으로 찾는
        // getCurrentSearchFormData()가 아니라 메뉴코드로 직접 핸들을 찾는다.
        const panel = onSearchStore.getState().chatSearchPanels[menu.cd_menu];
        const search = panel?.getFormdata?.();
        if (search && Object.keys(search).length > 0) {
          shareState = { search };
        }
      }
    } catch (error) {
      console.error("getShareState error", error);
    }

    const payload: SharePayload = {
      version: 1,
      menuId: menu.cd_menu,
      menuName: menu.nm_menu,
      menuPath: menu.nm_path,
      cd_module: menu.cd_module,
      nm_file: menu.nm_file,
      createdAt: new Date().toISOString(),
      state: shareState,
    };

    // DB 저장형 단축 URL 생성
    let url = "";
    try {
      const user = global.getState().userInfo;
      const res = await apiFetch<{ shareId: string }>("/api/share", {
        method: "POST",
        body: {
          payload,
          ttlDays: SHARE_TTL_DAYS,
          cdFirm: user?.cd_firm,
          cdUser: user?.cd_user,
          cdMenu: menu.cd_menu,
        } as any, // apiFetch에서 object는 JSON.stringify 처리
        headers: {
          "X-TENANT-ID": user?.tenant_id || "DEFAULT",
        },
      });
      const shareId = res.data?.shareId;
      if (!shareId) {
        throw new Error("shareId is empty");
      }
      const origin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "";
      url = `${origin}/share/${shareId}`;
    } catch (err: any) {
      console.error("share create failed", err);
      const msg =
        err?.message ||
        (err?.code
          ? `code: ${err.code}, message: ${err.message}`
          : "알 수 없는 오류");
      await showDialog("error", `공유 링크 생성에 실패했습니다.\n${msg}`);
      return;
    }

    if (await copyText(url)) {
      await showDialog("success", `링크가 복사되었습니다.\n${EXPIRE_NOTICE}`);
      return;
    }

    // 비보안 컨텍스트(http)나 제스처 만료로 복사가 막힌 경우 직접 복사할 수단을 안내한다
    console.warn("clipboard copy failed", url);
    await showPopupModal({
      title: "공유 링크",
      width: "500px",
      height: "280px",
      okayBtnText: "닫기",
      closeBtn: false,
      children: <ShareLinkFallback url={url} />,
    });
  };

  return (
    <Tooltip title="Share">
      <IconButton
        onClick={handleClick}
        sx={{
          color: "#0b0c69",
          border: "1px solid #0b0c69",
          width: 30,
          height: 30,
          "&:hover": {
            backgroundColor: "rgba(11,12,105,0.1)",
            transform: "scale(1.1)",
            transition: "all 0.2s ease",
          },
        }}
      >
        <ShareIcon sx={{ fontSize: "18px" }} />
      </IconButton>
    </Tooltip>
  );
};
